import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { C, KidAvatar, kidColour } from "../lib/kidLook";

// Every kid's times on one page, for a computer: hours and allowed hours for Monday to Sunday plus holidays,
// one row per kid, saved together. Holiday dates for the family's state(s) sit underneath. Each kid's own page
// still has the same settings; this page writes them with PUT /api/kids/{id}/schedule-config.

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const COLS = [...DAYS.map((d, i) => ({ key: String(i), label: d })), { key: "hol", label: "Holidays" }];

function fromKid(k) {
  const hours = {}, win = {};
  for (let i = 0; i < 7; i++) {
    hours[i] = Number((k.daily_hours || {})[String(i)] ?? 0);
    const w = (k.daily_windows || {})[String(i)] || {};
    win[i] = { start: w.start || "00:00", end: w.end || "23:59" };
  }
  hours.hol = Number(k.holiday_hours ?? 5);
  win.hol = { start: (k.holiday_window || {}).start || "09:00", end: (k.holiday_window || {}).end || "20:00" };
  return { hours, win };
}

function toPayload(t) {
  const daily_hours = {}, daily_windows = {};
  for (let i = 0; i < 7; i++) { daily_hours[String(i)] = t.hours[i]; daily_windows[String(i)] = t.win[i]; }
  return { daily_hours, daily_windows, holiday_hours: t.hours.hol, holiday_window: t.win.hol };
}

const cellInput = "times-input w-full min-w-0 rounded-md border border-[#E4E2DC] bg-white px-1 py-1 text-[12px] tabular-nums focus:border-[#5C7A4F] outline-none";

// Tab moves right along a line (Mon hours, Tue hours... Holidays), then to the next line, like a spreadsheet.
// Shift+Tab goes back. Inside a time box, the arrow keys still move between hours, minutes and am/pm.
function tabAcross(e) {
  if (e.key !== "Tab") return;
  const boxes = [...document.querySelectorAll("[data-tab-order]")]
    .sort((a, b) => Number(a.dataset.tabOrder) - Number(b.dataset.tabOrder));
  const i = boxes.indexOf(e.currentTarget);
  const next = boxes[i + (e.shiftKey ? -1 : 1)];
  if (!next) return; // past the last box: let the browser carry on to the Save button / page
  e.preventDefault();
  next.focus();
  if (next.select) try { next.select(); } catch (_) { /* time boxes can't select */ }
}

export default function Times() {
  const [kids, setKids] = useState([]);
  const [times, setTimes] = useState({});   // kid id -> {hours, win}
  const [dirty, setDirty] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kids");
      setKids(data);
      setTimes(Object.fromEntries(data.map((k) => [k.id, fromKid(k)])));
      setDirty(new Set());
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load the kids."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const edit = (kidId, fn) => {
    setTimes((t) => ({ ...t, [kidId]: fn(structuredClone(t[kidId])) }));
    setDirty((d) => new Set(d).add(kidId));
  };
  const setHours = (kidId, col, v) => edit(kidId, (t) => { t.hours[col] = Math.max(0, Math.min(24, parseFloat(v) || 0)); return t; });
  const setWin = (kidId, col, which, v) => edit(kidId, (t) => { t.win[col] = { ...t.win[col], [which]: v }; return t; });
  const mondayToWeekdays = (kidId) => edit(kidId, (t) => {
    for (let i = 1; i < 5; i++) { t.hours[i] = t.hours[0]; t.win[i] = { ...t.win[0] }; }
    return t;
  });
  const copyFrom = (kidId, srcId) => edit(kidId, () => structuredClone(times[srcId]));

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const id of dirty) await api.put(`/kids/${id}/schedule-config`, toPayload(times[id]));
      toast.success(`Saved ${dirty.size === 1 ? "1 kid's times" : `${dirty.size} kids' times`}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "That didn't save. Check the internet and try again.");
    } finally { setSaving(false); }
  };

  const weekTotal = (t) => DAYS.reduce((a, _, i) => a + (t.hours[i] || 0), 0);
  const states = useMemo(() => [...new Set(kids.map((k) => k.state).filter(Boolean))], [kids]);

  return (
    <div className="space-y-6" data-testid="times-page">
      <style>{`.times-input::-webkit-calendar-picker-indicator{display:none}`}</style>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">Times</h1>
          <p className="mt-1 max-w-2xl text-sm text-[#707A6E]">
            Every kid's hours and allowed times for the week and for holidays, in one place. Change what you like, then
            press Save. Holidays use the Holidays column on school and public holiday dates.
          </p>
        </div>
        <button onClick={saveAll} disabled={!dirty.size || saving} data-testid="times-save"
                className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: dirty.size ? "#D99A2B" : C.moss }}>
          {saving ? "Saving…" : dirty.size ? `Save changes (${dirty.size})` : "Saved"}
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-[#E4E2DC] bg-white">
        <table className="w-full min-w-[1000px] table-fixed text-sm" data-testid="times-table">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-[#707A6E]">
              <th className="px-3 py-3 w-[136px]">Kid</th>
              {COLS.map((c) => (
                <th key={c.key} className={`px-1.5 py-3 ${c.key === "hol" ? "bg-[#FDF1D8] text-[#A87414]" : ""}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kids.map((k, ki) => {
              const t = times[k.id];
              if (!t) return null;
              const others = kids.filter((o) => o.id !== k.id);
              return (
                <tr key={k.id} className={`border-t border-[#F0EFEB] align-top ${dirty.has(k.id) ? "bg-[#FFFBF0]" : ""}`}
                    data-testid={`times-row-${k.id}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <KidAvatar name={k.name} colour={kidColour(k.id, kids)} size={30} />
                      <Link to={`/kids/${k.id}`} className="font-semibold text-[#2C342A] hover:underline">{k.name}</Link>
                    </div>
                    <div className="mt-1 text-xs text-[#707A6E] tabular-nums">{weekTotal(t).toFixed(1)} h a week
                      {k.access_mode === "task_gated" && <span className="block">Tasks first</span>}
                      {!k.state && <span className="block text-[#C06E52]">No state set, so no holidays</span>}
                    </div>
                    <div className="mt-2 flex flex-col gap-1">
                      <button onClick={() => mondayToWeekdays(k.id)} className="text-left text-xs font-medium text-[#5C7A4F] hover:underline">
                        Copy Mon to Tue–Fri</button>
                      {others.length > 0 && (
                        <select defaultValue="" onChange={(e) => { if (e.target.value) copyFrom(k.id, e.target.value); e.target.value = ""; }}
                                className="rounded-md border border-[#E4E2DC] bg-white px-1.5 py-1 text-xs text-[#707A6E]">
                          <option value="">Same as…</option>
                          {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                      )}
                    </div>
                  </td>
                  {COLS.map((c, ci) => (
                    <td key={c.key} className={`px-1.5 py-3 ${c.key === "hol" ? "bg-[#FDF1D8]/50" : ""}`}>
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" max="24" step="0.25" value={t.hours[c.key]} aria-label={`${k.name} ${c.label} hours`}
                               data-tab-order={(ki * 3) * 100 + ci} onKeyDown={tabAcross}
                               onChange={(e) => setHours(k.id, c.key, e.target.value)} className={cellInput}
                               data-testid={`times-hours-${k.id}-${c.key}`} />
                        <span className="text-xs text-[#707A6E]">h</span>
                      </div>
                      <input type="time" value={t.win[c.key].start} aria-label={`${k.name} ${c.label} from`}
                             data-tab-order={(ki * 3 + 1) * 100 + ci} onKeyDown={tabAcross}
                             onChange={(e) => setWin(k.id, c.key, "start", e.target.value)} className={`${cellInput} mt-1`} />
                      <input type="time" value={t.win[c.key].end} aria-label={`${k.name} ${c.label} until`}
                             data-tab-order={(ki * 3 + 2) * 100 + ci} onKeyDown={tabAcross}
                             onChange={(e) => setWin(k.id, c.key, "end", e.target.value)} className={`${cellInput} mt-1`} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="-mt-3 text-xs text-[#707A6E]">Each box: hours allowed that day, then the time they can start and the
        time they must stop.</p>

      {states.map((s) => <HolidayDates key={s} state={s} />)}
    </div>
  );
}

function HolidayDates({ state }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    const y = new Date().getFullYear();
    try {
      const [a, b] = await Promise.all([api.get(`/holidays?state=${state}&year=${y}`), api.get(`/holidays?state=${state}&year=${y + 1}`)]);
      const seen = new Set();
      setList([...a.data, ...b.data].filter((h) => h.date >= today && !seen.has(h.id) && seen.add(h.id))
        .sort((x, y2) => x.date.localeCompare(y2.date)));
    } catch (_) { setList([]); }
  }, [state, today]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/holidays", { ...form, state, type: "school" });
      setForm(null); toast.success("Holiday added"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "That didn't save."); }
  };
  const remove = async (id) => { await api.delete(`/holidays/${id}`); load(); };

  return (
    <div className="rounded-2xl border border-[#E4E2DC] bg-white p-5" data-testid={`times-holidays-${state}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold text-[#2C342A]">Holiday dates ({state})</h3>
          <p className="text-xs text-[#707A6E]">On these days every kid in {state} gets their Holidays times.</p>
        </div>
        <button onClick={() => setForm({ date: "", end_date: "", name: "School holidays" })}
                className="inline-flex items-center gap-1 rounded-xl bg-[#5C7A4F] px-3 py-1.5 text-sm text-white">
          <Plus size={14} /> Add school holidays</button>
      </div>
      {form && (
        <form onSubmit={add} className="mb-3 grid grid-cols-1 gap-2 rounded-xl bg-[#F0EFEB]/60 p-3 md:grid-cols-[1fr_1fr_2fr_auto]">
          <label className="text-xs text-[#707A6E]">From
            <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                   className="mt-0.5 w-full rounded-lg border border-[#E4E2DC] bg-white px-2 py-1.5 text-sm" /></label>
          <label className="text-xs text-[#707A6E]">To
            <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                   className="mt-0.5 w-full rounded-lg border border-[#E4E2DC] bg-white px-2 py-1.5 text-sm" /></label>
          <label className="text-xs text-[#707A6E]">Name
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="mt-0.5 w-full rounded-lg border border-[#E4E2DC] bg-white px-2 py-1.5 text-sm" /></label>
          <div className="flex items-end gap-2">
            <button type="submit" className="rounded-lg bg-[#5C7A4F] px-3 py-1.5 text-sm text-white">Save</button>
            <button type="button" onClick={() => setForm(null)} className="rounded-lg border border-[#E4E2DC] bg-white px-3 py-1.5 text-sm">Cancel</button>
          </div>
        </form>
      )}
      <div className="grid max-h-72 grid-cols-1 gap-x-6 overflow-y-auto text-sm md:grid-cols-2">
        {list.map((h) => (
          <div key={h.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-[#F0EFEB]/60">
            <span>
              <span className={`mr-2 inline-block h-2 w-2 rounded-full ${h.type === "public" ? "bg-[#C06E52]" : "bg-[#D99A2B]"}`} />
              <span className="tabular-nums text-xs text-[#707A6E]">
                {new Date(h.date + "T00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</span>
              <span className="ml-2 text-[#2C342A]">{h.name}</span>
            </span>
            {(h.source === "manual" || h.source === "seed") && (
              <button onClick={() => remove(h.id)} className="rounded p-1 text-[#C06E52] hover:bg-[#C06E52]/10" aria-label={`Remove ${h.name}`}>
                <Trash2 size={12} /></button>
            )}
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-[#707A6E]">No coming holidays saved for {state} yet.</p>}
      </div>
    </div>
  );
}
